import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDbUser } from './useDbUser';

/**
 * Admin-only "search the ORIGINAL label" scope, shared by every surface that
 * offers it (keyword, CEPI chat, Cogni) so the gate cannot drift between them.
 *
 * State lives in the URL as ``?is_origin=true`` so it survives reload, is
 * shareable, and can be set by hand for testing without a UI control.
 *
 * WHY `useDbUser` AND NOT `useIsAdmin()`
 * `useIsAdmin()` reports false while /auth/sync is in flight and so collapses
 * "not an admin" into "not answered yet". Gating on it hides the control from
 * a real admin and makes it flicker in. `useDbUser` exposes `loading`
 * separately, which is the only way to tell those two apart.
 *
 * WHY THE INITIAL VALUE IS NOT GATED
 * Same reason SearchPage's `searchMode` isn't: coercing at init would bounce a
 * genuine admin off a deep link before the sync resolves, with no way back.
 * The effect below corrects it once the answer is actually known.
 *
 * ``eligible`` (default true) is the DATA precondition, separate from the
 * admin gate: the origin corpus is US-only and means nothing for a product
 * with a single version. When false the mode is forced off AND the URL param
 * is cleared, because leaving ``?is_origin=true`` behind on an ineligible
 * drug re-enters the mode on every reload with no visible control to exit.
 *
 * THIS IS NOT A SECURITY BOUNDARY. Hiding the control is a convenience — the
 * backend re-checks platform-admin on every request (keyword 403s, chat falls
 * back to the current label). Anyone can set the URL param or POST the field.
 */
export function useOriginScope(eligible: boolean = true): {
  isOrigin: boolean;
  setIsOrigin: (on: boolean) => void;
  /** True once we KNOW the user is an admin — use it to render the control. */
  canUseOrigin: boolean;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAdmin: isAdminSettled, loading: adminLoading } = useDbUser();
  const [isOrigin, setIsOriginState] = useState<boolean>(
    () => searchParams.get('is_origin') === 'true'
  );

  const setIsOrigin = useCallback(
    (on: boolean) => {
      setIsOriginState(on);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (on) next.set('is_origin', 'true');
        else next.delete('is_origin');
        return next;
      }, { replace: true });
    },
    [setSearchParams]
  );

  // Leave origin scope once we KNOW the user is not an admin. Rewriting the
  // URL matters as much as the state: clearing state alone leaves
  // ``?is_origin=true`` in the address bar, so every reload re-enters the mode
  // — the exact loop SearchPage's surface-mode guard documents.
  useEffect(() => {
    if (adminLoading) return;
    if ((!isAdminSettled || !eligible) && isOrigin) {
      setIsOriginState(false);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('is_origin');
        return next;
      }, { replace: true });
    }
  }, [adminLoading, isAdminSettled, eligible, isOrigin, setSearchParams]);

  return {
    // Both gates apply to the EFFECTIVE value, not just to the control's
    // visibility — a surface that renders no strip must still never send
    // version_scope='origin' on the strength of a leftover URL param.
    isOrigin: isOrigin && isAdminSettled && eligible,
    setIsOrigin,
    canUseOrigin: isAdminSettled,
  };
}
